import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { appToastQueue, type ToastQueue, type ToastState } from "@/lib/toast-queue";

/**
 * How long `animate-toast-out` (index.css) actually runs — kept in step
 * with it deliberately, since nothing here reads the animation's own CSS
 * duration back out.
 */
const EXIT_ANIMATION_MS = 200;

/**
 * `DESIGN.md` § Components `toast`: ink fill, canvas text, bottom-centre on
 * phone, bottom-left on desktop, max 420px, one at a time. Mounted once in
 * `AppShell.tsx` (outside the route content, like the shell chrome around
 * it) so a toast survives whatever navigation the action that triggered it
 * causes next.
 *
 * Ordinarily never used for an error (DESIGN.md: "errors do not use toasts
 * — they render next to the thing that failed"); see the discharge
 * record's VC-2.6-11 row for where that was originally checked. Story 15.5
 * (`RecordPage.tsx`'s `commitFieldOnLeave`, `DataTable.tsx`'s
 * `commitCellOnLeave`) is the one documented, accepted exception: an
 * inline edit auto-committed on navigate-away can fail *after* the field
 * itself has already unmounted, so there is no longer a "thing that
 * failed" left on screen to render next to — a distinctly-worded failure
 * toast (never claiming success) is the honest fallback there, not a
 * second, undocumented departure from the rule.
 *
 * `ToastQueue.getCurrent()` (animation-opportunity sweep, 2026-08-22) has
 * no notion of "still exiting" — `dismiss()` sets `current` straight to
 * `null` (or the next queued toast), so the DOM node this component
 * renders would otherwise unmount before any exit animation could ever
 * play. `displayed`/`state` below is this component's own small,
 * self-contained answer to that — the same job Radix's Presence primitive
 * does for `Dialog`/`Sheet`, just hand-rolled here since a toast isn't a
 * Radix component. `ToastQueue` itself is untouched: its "one at a time,
 * replace immediately" semantics (VC-2.6-10) still decide when a NEW toast
 * appears; this only delays this component's own unmount of the OLD one by
 * one exit-animation beat when nothing new has replaced it.
 */
export function Toaster({ queue = appToastQueue }: { queue?: ToastQueue }) {
  const current = useSyncExternalStore(queue.subscribe, queue.getCurrent, () => null);
  const [displayed, setDisplayed] = useState<ToastState | null>(current);
  const [state, setState] = useState<"open" | "closed">(current === null ? "closed" : "open");
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (current !== null) {
      // A toast is live — show it immediately (this also covers the
      // replace-with-the-next-queued-one case, which never has an exit of
      // its own to play: the container itself never left the screen).
      if (exitTimer.current !== null) {
        clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setDisplayed(current);
      setState("open");
      return;
    }
    // Nothing left to show — keep rendering the last real toast for one
    // exit-animation beat, then actually drop it.
    setState("closed");
    exitTimer.current = setTimeout(() => {
      setDisplayed(null);
      exitTimer.current = null;
    }, EXIT_ANIMATION_MS);
    return () => {
      if (exitTimer.current !== null) {
        clearTimeout(exitTimer.current);
      }
    };
  }, [current]);

  if (displayed === null) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4 md:inset-x-auto md:left-4 md:justify-start"
    >
      <div
        data-state={state}
        className="pointer-events-auto flex max-w-toast-max items-center gap-4 rounded-md bg-ink px-4 py-3 text-body text-canvas data-[state=closed]:animate-toast-out data-[state=open]:animate-toast-in"
      >
        <p>{displayed.message}</p>
        <button
          type="button"
          onClick={() => queue.dismiss()}
          className="inline-flex h-tap-target-min shrink-0 items-center rounded-md text-meta font-medium text-canvas underline underline-offset-2"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
